import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Separator } from "../components/ui/separator";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, FileSearch, BadgeCheck, Banknote, Network, ClipboardCheck,
  Stamp, ArrowRight, CheckCircle2, Lock, ChevronRight, Building2,
} from "lucide-react";
import WhyWeAskDialog from "../components/WhyWeAskDialog";

const SECTIONS = [
  { id: "member-profile",         icon: Building2,      title: "Start Investor Qualification",                blurb: "Primary intake — individual, entity, family office, trust." },
  { id: "identity",               icon: FileSearch,     title: "KYC / AML Verification",                       blurb: "Identity, residency, sanctions screening (SumSub-ready)." },
  { id: "accreditation",          icon: BadgeCheck,     title: "Accredited / Qualified Investor Review",       blurb: "Eligibility under U.S. Reg D / Reg S or non-U.S. equivalent." },
  { id: "data-transfer-consent",  icon: ShieldCheck,    title: "Data Transfer Consent",                        blurb: "Authorize file forwarding to the regulated partner." },
  { id: "partner-onboarding",     icon: Stamp,          title: "Regulated Partner Onboarding",                 blurb: "Approved Regulated Partner Onboarding Pathway." },
  { id: "introducer",             icon: Network,        title: "Introducer Application",                       blurb: "Referral disclosure." },
  { id: "review-status",          icon: ClipboardCheck, title: "Compliance Status",                            blurb: "Real-time status of your application." },
];

const ACK_KEYS = [
  ["compliance_review_only",    "I understand this is a compliance review only."],
  ["no_approval_guarantee",     "I understand submission does not guarantee approval."],
  ["may_require_kyc_aml_sof",   "I understand EchoSense may require KYC, AML, source-of-funds, and investor eligibility verification."],
  ["may_redirect_third_party",  "I understand I may be redirected to a third-party verification provider."],
  ["no_funds_until_instructions", "I understand I should not send funds until written onboarding and funding instructions are provided."],
  ["no_returns_guarantee",      "I understand no investment, trading result, allocation, compensation, or return is guaranteed."],
];

const initialForm = {
  first_name: "", last_name: "", email: "", phone: "",
  country_of_residence: "", citizenship: "",
  entity_type: "individual",
  intended_role: "investor",
  // Tri-state — defaulted to "unsure" so the user must consciously answer
  is_us_person: "unsure",
  is_accredited_investor: "unsure",
  is_qualified_purchaser: "unsure",
  is_professional_investor_outside_us: "unsure",
  intended_allocation_range: "",
  primary_source_of_funds: "",
  referred_by_someone: false,
  referrer_name: "", referrer_email: "", referrer_code: "",
  seeking_introducer_participation: false,
};

const TRI_OPTIONS = [
  { value: "yes",    label: "Yes" },
  { value: "no",     label: "No" },
  { value: "unsure", label: "Unsure" },
];
const TRI_PLUS_NA_OPTIONS = [
  ...TRI_OPTIONS,
  { value: "not_applicable", label: "Not Applicable" },
];

const VerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState(initialForm);
  const [acks, setAcks] = useState(Object.fromEntries(ACK_KEYS.map(([k]) => [k, false])));
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { id, status }

  // --- Data Transfer Consent state (shown on confirmation screen) ---
  const [consent, setConsent] = useState({
    consent_share_with_partner: false,
    consent_no_funds_to_echosense: false,
    consent_echosense_role_introducer: false,
    user_typed_full_name: "",
  });
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentResult, setConsentResult] = useState(null);

  const allConsentChecked =
    consent.consent_share_with_partner &&
    consent.consent_no_funds_to_echosense &&
    consent.consent_echosense_role_introducer &&
    consent.user_typed_full_name.trim().length >= 2;

  const submitConsent = async () => {
    if (!confirmation?.id) return;
    if (!allConsentChecked) {
      toast.error("Please tick all three boxes and type your full legal name.");
      return;
    }
    setConsentSubmitting(true);
    try {
      const r = await api.post("/verification/data-transfer-consent", {
        record_id: confirmation.id,
        ...consent,
      });
      setConsentResult(r.data);
      toast.success("Consent recorded. File queued for regulated partner review.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save consent");
    } finally {
      setConsentSubmitting(false);
    }
  };

  // Smooth-scroll to section if URL hash present
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
    // `el` is created inside the effect — depending only on `location.hash`
    // is intentional (we want to re-run only when the hash changes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const allAcksChecked = ACK_KEYS.every(([k]) => acks[k]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allAcksChecked) {
      toast.error("Please acknowledge every compliance statement to continue.");
      return;
    }
    if (!form.first_name || !form.last_name || !form.email || !form.country_of_residence ||
        !form.citizenship || !form.intended_allocation_range || !form.primary_source_of_funds) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      // Strip empty-string optional fields so backend Optional[EmailStr]/Optional[str] validators accept them as null
      const optionalFields = ["phone", "referrer_name", "referrer_email", "referrer_code"];
      const payload = { ...form, acknowledgments: acks };
      optionalFields.forEach((k) => {
        if (typeof payload[k] === "string" && payload[k].trim() === "") {
          payload[k] = null;
        }
      });
      const r = await api.post("/verification/intake", payload);
      setConfirmation(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Submission failed";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map((d) => d?.msg || d?.detail || JSON.stringify(d)).join("; ");
      else if (detail && typeof detail === "object") msg = detail.msg || JSON.stringify(detail);
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("verification intake error", err?.response?.status, detail);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Confirmation screen ---
  if (confirmation) {
    return (
      <div className="min-h-screen bg-obsidian text-platinum relative overflow-hidden">
        <BackgroundGrid />
        <PortalNav />
        <div className="relative z-10 max-w-3xl mx-auto px-6 pt-32 pb-24" data-testid="verification-confirmation">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <CheckCircle2 className="text-white" size={26} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-azure font-semibold">EchoSense Capital Club</p>
              <h1 className="text-3xl font-serif text-cream" data-testid="confirmation-title">Verification Request Submitted</h1>
            </div>
          </div>
          <Card className="bg-surface/80 border-platinum/15 backdrop-blur">
            <CardContent className="p-8 space-y-6">
              <p className="text-platinum leading-relaxed">
                Thank you. Your EchoSense Capital Club verification request has been submitted.
                Our compliance process may include identity verification, AML/KYC screening,
                source-of-funds review, and accredited or qualified investor verification.
                <span className="block mt-3 font-semibold text-azure">
                  Do not send funds until you receive written onboarding and funding instructions.
                </span>
              </p>
              <Separator className="bg-platinum/15" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <KeyValue label="Reference ID" value={confirmation.id} mono />
                <KeyValue label="Status" value={consentResult?.status || confirmation.status} />
                <KeyValue label="Provider" value={confirmation.provider || "manual_review"} />
              </div>

              {/* --- Regulated Partner Data Transfer Consent --- */}
              {!consentResult ? (
                <div className="pt-2 space-y-5" data-testid="consent-block">
                  <div className="rounded-lg border border-blue-600/30 bg-blue-600/[0.06] p-5">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-azure font-semibold mb-2">Regulated Partner Data Transfer Consent</p>
                    <p className="text-sm text-platinum leading-relaxed">
                      By continuing, I authorize EchoSense Capital Markets to transmit my submitted
                      onboarding information, identity verification data, KYC / AML status, accredited
                      or qualified investor verification status, and related compliance information to
                      the approved regulated onboarding partner for further review and account
                      processing.
                    </p>
                    <p className="text-sm text-platinum/85 leading-relaxed mt-3">
                      I understand that EchoSense Capital Markets is not approving my account, receiving
                      my funds, providing investment advice, managing my trading account, issuing tax
                      forms, or guaranteeing any result.
                    </p>
                    <p className="text-sm text-platinum/85 leading-relaxed mt-3">
                      I understand that final onboarding, funding instructions, account approval,
                      reporting, tax documents, and investment-related documents may be provided
                      directly by the regulated partner, fund, adviser, solicitor, platform,
                      administrator, or other approved third party.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-share">
                      <Checkbox
                        checked={consent.consent_share_with_partner}
                        onCheckedChange={(v) => setConsent((c) => ({ ...c, consent_share_with_partner: !!v }))}
                        className="mt-1 border-azure/70 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                      />
                      <span className="text-sm text-platinum">
                        I consent to EchoSense sharing my verification and onboarding information with
                        the approved regulated onboarding partner for account review and onboarding.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-no-funds">
                      <Checkbox
                        checked={consent.consent_no_funds_to_echosense}
                        onCheckedChange={(v) => setConsent((c) => ({ ...c, consent_no_funds_to_echosense: !!v }))}
                        className="mt-1 border-azure/70 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                      />
                      <span className="text-sm text-platinum">
                        I understand I should not send funds to EchoSense, any introducer, any
                        individual, or any unapproved account. I will only follow written funding
                        instructions issued by the approved regulated onboarding partner or fund
                        administrator.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-introducer-role">
                      <Checkbox
                        checked={consent.consent_echosense_role_introducer}
                        onCheckedChange={(v) => setConsent((c) => ({ ...c, consent_echosense_role_introducer: !!v }))}
                        className="mt-1 border-azure/70 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                      />
                      <span className="text-sm text-platinum">
                        I understand EchoSense is an introducer and relationship portal only, and does
                        not provide investment advice, trading advice, legal advice, tax advice, or
                        account approval.
                      </span>
                    </label>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider text-platinum-dark font-semibold">
                      Type your full legal name to sign <span className="text-azure">*</span>
                    </Label>
                    <Input
                      value={consent.user_typed_full_name}
                      onChange={(e) => setConsent((c) => ({ ...c, user_typed_full_name: e.target.value }))}
                      placeholder="e.g. John D. Jenson"
                      className={inputCls + " mt-1.5"}
                      data-testid="consent-typed-name"
                    />
                  </div>

                  <Button
                    onClick={submitConsent}
                    disabled={consentSubmitting || !allConsentChecked}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-semibold tracking-wide shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-data-transfer-consent"
                  >
                    <ShieldCheck size={14} className="mr-2" />
                    {consentSubmitting ? "Recording consent…" : "Record Consent & Forward to Regulated Partner"}
                  </Button>
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] p-5" data-testid="consent-recorded">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-platinum-light font-semibold mb-2">Consent Recorded</p>
                  <p className="text-sm text-platinum leading-relaxed">
                    Your data-transfer consent has been recorded ({consentResult.consent_id?.slice(0, 8)}…).
                    Your file is now queued for review by the
                    <span className="text-azure-light font-semibold"> Approved Regulated Partner Onboarding Pathway</span>.
                    The regulated partner will contact you directly with next steps, login credentials, and
                    written funding instructions if applicable.
                  </p>
                </div>
              )}
              {/* --- /consent --- */}

              <div className="flex gap-3 pt-2 flex-wrap">
                <Button onClick={() => navigate("/dashboard")} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold" data-testid="confirmation-cta">
                  Return to Dashboard <ArrowRight size={14} className="ml-1" />
                </Button>
                <Button variant="outline" onClick={() => navigate("/")} className="border-platinum/30 text-platinum hover:bg-platinum/10">
                  Return Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // --- Intake form ---
  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-hidden" data-testid="verification-page">
      <BackgroundGrid />
      <PortalNav />

      <div className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-24">
        {/* Hero */}
        <div className="mb-14 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/10 border border-blue-600/30 mb-5">
            <Lock size={12} className="text-azure" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-azure-light font-semibold">Compliance Review Portal</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-serif text-cream leading-[1.05]" data-testid="verification-title">
            Investor / Member<br /><span className="text-azure">Verification Portal</span>
          </h1>
          <p className="text-lg text-platinum/85 mt-6 leading-relaxed">
            For prospective EchoSense Capital Club members, introducers, accredited investors, qualified
            investors, offshore clients, and strategic partners. Complete an online compliance review before
            receiving access to private materials, onboarding links, funding pathways,
            or JV-related opportunities.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button asChild className="bg-blue-600 hover:bg-blue-500 text-white font-semibold tracking-wide shadow-lg shadow-blue-500/30" data-testid="hero-cta-start">
              <a href="#member-profile">Start Investor Qualification <ArrowRight size={14} className="ml-2" /></a>
            </Button>
            <span className="self-center text-sm text-platinum-dark">KYC / AML / Accredited Investor Review</span>
          </div>
        </div>

        {/* Section index */}
        <Card className="bg-surface/80 border-platinum/15 backdrop-blur mb-12">
          <CardContent className="p-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-azure font-semibold mb-4">Portal Sections</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {SECTIONS.map((s, i) => (
                <a key={s.id} href={`#${s.id}`} data-testid={`section-link-${s.id}`}
                   className="flex items-start gap-3 p-3 rounded-md border border-platinum/10 hover:border-azure/40 hover:bg-surface-highlight transition-all group">
                  <div className="h-9 w-9 rounded bg-blue-600/10 border border-blue-600/30 flex items-center justify-center shrink-0">
                    <s.icon size={16} className="text-azure" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] text-platinum-dark font-mono">0{i + 1}</p>
                    <p className="text-sm font-semibold text-cream group-hover:text-azure-light transition-colors">{s.title}</p>
                    <p className="text-xs text-platinum-dark mt-0.5">{s.blurb}</p>
                  </div>
                  <ChevronRight size={14} className="text-platinum-dark/70 group-hover:text-azure mt-2" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-12">
          {/* SECTION 1 — Member Profile */}
          <Section id="member-profile" idx={1} icon={Building2} title="Member Profile" subtitle="Applicant information & jurisdictional profile.">
            <Grid cols={2}>
              <Field label="First name" required>
                <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} className={inputCls} required data-testid="field-first-name" />
              </Field>
              <Field label="Last name" required>
                <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} className={inputCls} required data-testid="field-last-name" />
              </Field>
              <Field label="Email" required>
                <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} required data-testid="field-email" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="+1 555 000 0000" data-testid="field-phone" />
              </Field>
              <Field label="Country of residence" required>
                <Input value={form.country_of_residence} onChange={(e) => update("country_of_residence", e.target.value)} className={inputCls} required data-testid="field-country" />
              </Field>
              <Field label="Citizenship" required>
                <Input value={form.citizenship} onChange={(e) => update("citizenship", e.target.value)} className={inputCls} required data-testid="field-citizenship" />
              </Field>
            </Grid>
            <Grid cols={2} className="mt-2">
              <Field label="Applicant type" required>
                <Select value={form.entity_type} onValueChange={(v) => update("entity_type", v)}>
                  <SelectTrigger className={inputCls} data-testid="field-entity-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-platinum/15 text-platinum">
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="entity">Entity</SelectItem>
                    <SelectItem value="trust">Trust</SelectItem>
                    <SelectItem value="family_office">Family Office</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Intended role" required>
                <Select value={form.intended_role} onValueChange={(v) => update("intended_role", v)}>
                  <SelectTrigger className={inputCls} data-testid="field-intended-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-platinum/15 text-platinum">
                    <SelectItem value="investor">Investor</SelectItem>
                    <SelectItem value="introducer">Introducer</SelectItem>
                    <SelectItem value="strategic_partner">Strategic Partner</SelectItem>
                    <SelectItem value="advisor">Advisor</SelectItem>
                    <SelectItem value="family_office">Family Office</SelectItem>
                    <SelectItem value="offshore_client">Offshore Client</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </Grid>
          </Section>

          {/* SECTION 2 — Identity / KYC */}
          <Section id="identity" idx={2} icon={FileSearch} title="Identity Verification / KYC" subtitle="Required for AML / sanctions / PEP screening before any allocation discussion.">
            <p className="text-sm text-platinum-dark leading-relaxed">
              You may be redirected to a third-party verification provider (Persona, Sumsub,
              Parallel Markets, VerifyInvestor, or equivalent). Until that step is initiated,
              this submission is reviewed manually by EchoSense compliance.
            </p>
            <div className="mt-3">
              <WhyWeAskDialog testId="why-we-ask-kyc" />
            </div>
          </Section>

          {/* SECTION 3 — Accreditation (revised: plain-English explanations, tri-state answers) */}
          <Section
            id="accreditation"
            idx={3}
            icon={BadgeCheck}
            title="Accredited / Qualified Investor Verification"
            subtitle="This section helps us understand whether you may qualify for certain private or institutional opportunities. These questions do not approve or reject you automatically. They simply help our compliance team route your file correctly."
          >
            <div className="-mt-2 mb-2">
              <WhyWeAskDialog testId="why-we-ask-accreditation" />
            </div>
            {/* Q1 — U.S. person */}
            <ChoiceQuestion
              label="Are you a U.S. person?"
              explanation={`A "U.S. person" generally means a U.S. citizen, U.S. resident, U.S. entity, or someone otherwise treated as U.S.-based for securities compliance purposes. This matters because U.S. investors may be reviewed under U.S. private offering rules, while non-U.S. investors may be reviewed under offshore or international eligibility rules.`}
              options={TRI_OPTIONS}
              value={form.is_us_person}
              onChange={(v) => update("is_us_person", v)}
              testid="field-us-person"
            />

            {/* Q2 — Accredited investor */}
            <ChoiceQuestion
              label="Do you believe you qualify as an accredited investor?"
              explanation={`For U.S. individuals, an accredited investor generally means someone who meets certain income, net worth, professional certification, or financial sophistication standards. Common examples include an individual with net worth over $1 million, excluding their primary residence, or income over $200,000 individually, or $300,000 with a spouse or partner, in each of the last two years with a reasonable expectation of the same income this year.`}
              options={TRI_OPTIONS}
              value={form.is_accredited_investor}
              onChange={(v) => update("is_accredited_investor", v)}
              testid="field-accredited"
            />

            {/* Q3 — Qualified purchaser */}
            <ChoiceQuestion
              label="Do you believe you qualify as a qualified purchaser?"
              explanation={`A qualified purchaser is generally a higher financial threshold than an accredited investor. For an individual, this often means owning at least $5 million in qualifying investments. Certain family companies, trusts, entities, or investment managers may qualify under different rules.`}
              options={TRI_OPTIONS}
              value={form.is_qualified_purchaser}
              onChange={(v) => update("is_qualified_purchaser", v)}
              testid="field-qualified-purchaser"
            />

            {/* Q4 — Professional / non-U.S. investor */}
            <ChoiceQuestion
              label="Are you a professional, qualified, or sophisticated investor outside the U.S.?"
              explanation={`If you are not a U.S. person, your investor eligibility may be reviewed under the rules of your own country or an applicable offshore framework. This may include professional investor, qualified investor, sophisticated investor, wholesale investor, or similar classifications depending on your jurisdiction.`}
              options={TRI_PLUS_NA_OPTIONS}
              value={form.is_professional_investor_outside_us}
              onChange={(v) => update("is_professional_investor_outside_us", v)}
              testid="field-professional-non-us"
            />

            {/* Section-level compliance note */}
            <div
              className="mt-4 p-4 rounded border border-blue-600/30 bg-blue-600/[0.05]"
              data-testid="section-3-compliance-note"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-azure font-semibold mb-2">
                Important
              </p>
              <p className="text-sm text-platinum leading-relaxed">
                Your answers are for initial routing only. EchoSense may require third-party
                verification, identity review, AML/KYC screening, source-of-funds review, and
                supporting documentation before granting access to any private materials,
                onboarding pathway, compensation program, or capital-markets opportunity.{" "}
                <span className="text-azure-light font-semibold">
                  Do not send funds until written onboarding and funding instructions are provided.
                </span>
              </p>
            </div>
          </Section>

          {/* SECTION 4 — Source of Funds */}
          <Section id="source-of-funds" idx={4} icon={Banknote} title="Source of Funds / Source of Wealth" subtitle="Estimated allocation and origin of capital.">
            <Grid cols={2}>
              <Field label="Estimated intended allocation range" required>
                <Select value={form.intended_allocation_range} onValueChange={(v) => update("intended_allocation_range", v)}>
                  <SelectTrigger className={inputCls} data-testid="field-allocation-range"><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent className="bg-surface border-platinum/15 text-platinum">
                    <SelectItem value="250k-1m">$250,000 – $1,000,000</SelectItem>
                    <SelectItem value="1m-5m">$1,000,000 – $5,000,000</SelectItem>
                    <SelectItem value="5m-25m">$5,000,000 – $25,000,000</SelectItem>
                    <SelectItem value="25m+">$25,000,000+</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </Grid>
            <Field label="Primary source of funds" required>
              <Textarea value={form.primary_source_of_funds} onChange={(e) => update("primary_source_of_funds", e.target.value)}
                        rows={3} placeholder="e.g. operating-business profits, M&A liquidity event, professional earnings, trust distributions"
                        className={`${inputCls} min-h-[100px]`} required data-testid="field-source-of-funds" />
            </Field>
          </Section>

          {/* SECTION 5 — Introducer Disclosure */}
          <Section id="introducer" idx={5} icon={Network} title="Introducer Disclosure" subtitle="Disclose any referring party.">
            <YesNoBool label="Were you referred by someone?" value={form.referred_by_someone} onChange={(v) => update("referred_by_someone", v)} testid="field-referred" />
            {form.referred_by_someone && (
              <Grid cols={3}>
                <Field label="Referrer name">
                  <Input value={form.referrer_name} onChange={(e) => update("referrer_name", e.target.value)} className={inputCls} data-testid="field-referrer-name" />
                </Field>
                <Field label="Referrer email">
                  <Input type="email" value={form.referrer_email} onChange={(e) => update("referrer_email", e.target.value)} className={inputCls} data-testid="field-referrer-email" />
                </Field>
                <Field label="Referrer code">
                  <Input value={form.referrer_code} onChange={(e) => update("referrer_code", e.target.value.toUpperCase())} className={`${inputCls} uppercase`} placeholder="VC-ABC12345" data-testid="field-referrer-code" />
                </Field>
              </Grid>
            )}
          </Section>

          {/* SECTION — Data Transfer Consent (informational here; live form on confirmation screen) */}
          <Section id="data-transfer-consent" idx={6} icon={ShieldCheck} title="Regulated Partner Data Transfer Consent" subtitle="Available after intake submission. You will be asked to authorize transfer of your verification data to the approved regulated onboarding partner.">
            <div className="space-y-3 text-sm text-platinum/85 leading-relaxed">
              <p>
                Once your intake, KYC/AML, and accredited / qualified investor verifications are complete,
                you will be asked to provide a written consent authorizing EchoSense Capital Club to
                transmit your onboarding information to the <span className="text-azure-light font-semibold">Approved Regulated Partner Onboarding Pathway</span> for further review.
              </p>
              <p className="text-platinum-dark text-xs">
                EchoSense Capital Club is an introducer and relationship portal only. EchoSense does
                not provide investment advice, trading advice, legal advice, tax advice, or account approval.
              </p>
            </div>
          </Section>

          {/* SECTION — Regulated Partner Onboarding (informational) */}
          <Section id="partner-onboarding" idx={7} icon={Stamp} title="Regulated Partner Onboarding" subtitle="Final account approval, funding instructions, and reporting are handled by the regulated partner directly.">
            <div className="space-y-3 text-sm text-platinum/85 leading-relaxed">
              <p>
                After your data-transfer consent is recorded, your file may be forwarded to the
                <span className="text-azure-light font-semibold"> Approved Regulated Partner Onboarding Pathway</span>.
                The partner will issue any required forms, login credentials, funding instructions, and
                reporting directly to you.
              </p>
              <p className="font-semibold text-azure-light">
                Do not send funds to EchoSense, any introducer, any individual, or any unapproved
                account. Follow only the written funding instructions issued by the approved regulated
                onboarding partner or fund administrator.
              </p>
            </div>
          </Section>

          {/* SECTION — Compliance Status */}
          <Section id="review-status" idx={8} icon={ClipboardCheck} title="Compliance Status" subtitle="After submission you'll receive a tracking ID. Status updates are visible at /verification/status/{id}.">
            <p className="text-sm text-platinum-dark leading-relaxed">
              Possible statuses: <span className="text-platinum">draft → submitted → kyc_pending → kyc_complete → accredited_pending → accredited_complete → data_transfer_consent_pending → ready_for_partner_review → transferred_to_partner → partner_onboarding_pending → partner_onboarding_complete → approved / rejected / more_info_required / suspended_compliance_review</span>.
            </p>
          </Section>

          {/* SECTION — Final Approval */}
          <Section id="approval" idx={9} icon={Stamp} title="Final Approval / Next Steps" subtitle="Approval is issued only in writing by the regulated partner after their independent review.">
            <p className="text-sm text-platinum-dark leading-relaxed">
              No funds, allocations, compensation, or account access are released until written
              onboarding instructions are issued by the approved regulated onboarding partner or fund
              administrator.
            </p>
          </Section>

          {/* Compliance Notice */}
          <Card className="bg-blue-600/[0.06] border-blue-600/40">
            <CardContent className="p-6">
              <p className="text-[10px] uppercase tracking-[0.3em] text-azure font-semibold mb-3">Required Compliance Notice</p>
              <p className="text-sm text-platinum leading-relaxed">
                Submission of this form does not guarantee acceptance, allocation, account approval,
                investment participation, compensation eligibility, trading access, funding approval, or
                returns. <span className="text-azure-light font-semibold">Do not send funds until written
                onboarding and funding instructions are provided.</span>
              </p>
            </CardContent>
          </Card>

          {/* Acknowledgments */}
          <Card className="bg-surface/80 border-platinum/15 backdrop-blur">
            <CardContent className="p-6 space-y-4">
              <p className="text-[10px] uppercase tracking-[0.3em] text-azure font-semibold">Required Acknowledgments</p>
              {ACK_KEYS.map(([k, label]) => (
                <label key={k} className="flex items-start gap-3 cursor-pointer group" data-testid={`ack-${k.replace(/_/g, '-')}`}>
                  <Checkbox
                    checked={!!acks[k]}
                    onCheckedChange={(v) => setAcks((a) => ({ ...a, [k]: !!v }))}
                    className="mt-1 border-azure/70 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <span className="text-sm text-platinum group-hover:text-cream">{label}</span>
                </label>
              ))}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <p className="text-xs text-platinum-dark">
              By submitting you confirm the above information is accurate to the best of your knowledge.
            </p>
            <Button type="submit" disabled={submitting || !allAcksChecked}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-semibold tracking-wide shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-verification">
              <ShieldCheck size={14} className="mr-2" />
              {submitting ? "Submitting…" : "Submit for Compliance Review"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Sub-components ---
// The shadcn SelectValue placeholder defaults to `text-muted-foreground`, which is
// pinned across themes and lands at 3.55:1 on the dark surface — the one AA miss
// the contrast sweep found. Bridge it with the rest of the field styling.
const inputCls = "bg-surface border-platinum/20 text-platinum placeholder:text-platinum-dark data-[placeholder]:text-platinum-dark focus:border-azure focus:ring-azure/25";

const Section = ({ id, idx, icon: Icon, title, subtitle, children }) => (
  <section id={id} className="scroll-mt-24" data-testid={`section-${id}`}>
    <div className="flex items-start gap-4 mb-6">
      <div className="h-12 w-12 rounded bg-gradient-to-br from-blue-400/20 to-blue-700/10 border border-blue-600/30 flex items-center justify-center shrink-0">
        <Icon size={20} className="text-azure" />
      </div>
      <div>
        <p className="text-[10px] text-platinum-dark font-mono">SECTION 0{idx}</p>
        <h2 className="text-2xl font-serif text-cream">{title}</h2>
        {subtitle && <p className="text-sm text-platinum-dark mt-1">{subtitle}</p>}
      </div>
    </div>
    <Card className="bg-surface/60 border-platinum/15 backdrop-blur">
      <CardContent className="p-6 space-y-5">{children}</CardContent>
    </Card>
  </section>
);

const Grid = ({ cols = 2, className = "", children }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-4 ${className}`}>{children}</div>
);

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-platinum-dark font-semibold">
      {label} {required && <span className="text-azure">*</span>}
    </Label>
    {children}
  </div>
);

const YesNoBool = ({ label, value, onChange, testid }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-platinum/10 last:border-0">
    <Label className="text-sm text-platinum">{label}</Label>
    <RadioGroup
      value={value ? "yes" : "no"}
      onValueChange={(v) => onChange(v === "yes")}
      className="flex gap-4"
      data-testid={testid}
    >
      <label className="flex items-center gap-2 text-sm text-platinum/85 cursor-pointer">
        <RadioGroupItem value="yes" className="border-azure/70 text-azure" /> Yes
      </label>
      <label className="flex items-center gap-2 text-sm text-platinum/85 cursor-pointer">
        <RadioGroupItem value="no" className="border-azure/70 text-azure" /> No
      </label>
    </RadioGroup>
  </div>
);

const ChoiceQuestion = ({ label, explanation, options, value, onChange, testid }) => (
  <div className="py-4 border-b border-platinum/10 last:border-0 space-y-3" data-testid={`${testid}-block`}>
    <Label className="text-base text-cream font-medium leading-snug block">{label}</Label>
    {explanation && (
      <div className="rounded-md bg-surface-highlight border border-platinum/15 p-3" data-testid={`${testid}-explanation`}>
        <p className="text-[10px] uppercase tracking-[0.25em] text-azure font-semibold mb-1.5">
          Plain-English explanation
        </p>
        <p className="text-[13px] text-platinum/85 leading-relaxed">{explanation}</p>
      </div>
    )}
    <RadioGroup
      value={value}
      onValueChange={onChange}
      className="flex flex-wrap gap-x-6 gap-y-2 pt-1"
      data-testid={testid}
    >
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`flex items-center gap-2 text-sm cursor-pointer transition-colors ${
            value === opt.value ? "text-azure-light" : "text-platinum/85 hover:text-cream"
          }`}
          data-testid={`${testid}-${opt.value}`}
        >
          <RadioGroupItem value={opt.value} className="border-azure/70 text-azure" />
          {opt.label}
        </label>
      ))}
    </RadioGroup>
  </div>
);

const KeyValue = ({ label, value, mono }) => (
  <div className="space-y-1">
    <p className="text-[10px] uppercase tracking-widest text-platinum-dark font-semibold">{label}</p>
    <p className={`text-platinum ${mono ? "font-mono text-xs break-all" : "text-sm"}`}>{value}</p>
  </div>
);

const BackgroundGrid = () => (
  <>
    <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-surface/40 to-obsidian pointer-events-none" />
    <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
         style={{ backgroundImage: "linear-gradient(rgb(var(--brand-gold-rgb) / 0.6) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--brand-gold-rgb) / 0.6) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/[0.03] rounded-full blur-3xl pointer-events-none" />
  </>
);

const PortalNav = () => (
  <nav className="absolute top-0 left-0 right-0 z-30 border-b border-platinum/10 bg-obsidian/80 backdrop-blur">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3">
        <div className="h-10 w-10 rounded bg-gradient-to-br from-blue-400 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/30">
          <ShieldCheck className="text-white" size={20} />
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-[0.3em] text-azure font-semibold">EchoSense Capital Club</p>
          <p className="text-sm text-cream font-serif">Verification Portal</p>
        </div>
      </Link>
      <Link to="/" className="text-sm text-platinum/85 hover:text-azure transition-colors">← Return to site</Link>
    </div>
  </nav>
);

export default VerificationPage;
